import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import {
  getOpenAIConfig,
  getOpenAIModels,
  updateOpenAIAdminKey,
  updateOpenAIConfig,
} from "../../../../api/openAIConfig";
import {
  getAiAgents,
  getAiCosts,
  getAiPrompts,
  getAiRuntimePrompt,
  getAiUsage,
  updateAiCost,
  createAiPromptDraft,
  publishAiPrompt,
  updateAiAgentActive,
  reconcileAiCurrentMonth,
} from "../../../../api/aiGovernance";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { useSkeleton } from "../../../hooks/useSkeleton";

function getEmptyOpenAIConfig() {
  return {
    id: null,
    base_url: "https://api.openai.com/v1",
    api_key: "",
    api_key_configured: false,
    default_model: "gpt-4.1",
    project_id: "",
    admin_api_key_configured: false,
  };
}

export default function OpenAIConfigPage() {
  const [configId, setConfigId] = useState(null);
  const [baseUrl, setBaseUrl] = useState("https://api.openai.com/v1");
  const [apiKey, setApiKey] = useState("");
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [defaultModel, setDefaultModel] = useState("gpt-4.1");
  const [projectId, setProjectId] = useState("");
  const [adminApiKey, setAdminApiKey] = useState("");
  const [adminApiKeyConfigured, setAdminApiKeyConfigured] = useState(false);
  const [clearAdminApiKey, setClearAdminApiKey] = useState(false);
  const [savingAdminKey, setSavingAdminKey] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconciliation, setReconciliation] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [modelsDialogOpen, setModelsDialogOpen] = useState(false);
  const [models, setModels] = useState([]);
  const [agents, setAgents] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [costs, setCosts] = useState([]);
  const [usage, setUsage] = useState({ total_usd: 0, llamadas: 0, detalle: [], desglose_mes: [] });
  const [savingCost, setSavingCost] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [promptContent, setPromptContent] = useState("");
  const [runtimePrompt, setRuntimePrompt] = useState(null);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [savingAgentId, setSavingAgentId] = useState(null);

  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );
  const accessToken = loggedUser?.accessToken;

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  const applyServerConfig = useCallback((data) => {
    const nextConfig = { ...getEmptyOpenAIConfig(), ...(data || {}) };
    setConfigId(nextConfig.id || null);
    setBaseUrl(nextConfig.base_url || "https://api.openai.com/v1");
    setApiKey(nextConfig.api_key || "");
    setApiKeyConfigured(!!nextConfig.api_key_configured);
    setDefaultModel(nextConfig.default_model || "gpt-4.1");
    setProjectId(nextConfig.project_id || "");
    setAdminApiKeyConfigured(!!nextConfig.admin_api_key_configured);
  }, []);

  const loadOpenAIConfig = useCallback(async () => {
    const res = await getOpenAIConfig(accessToken);
    if (res.status === 200) {
      applyServerConfig(res.data);
      if (res.data.configuration_warning)
        alertError({ customMessage: res.data.configuration_warning });
    }
  }, [accessToken, applyServerConfig]);

  useEffect(() => {
    loadOpenAIConfig()
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se ha podido obtener la configuracion de OpenAI.",
        });
      })
      .finally(() => {
        disableLoadingData();
      });
  }, [disableLoadingData, loadOpenAIConfig]);

  const loadGovernance = useCallback(async () => {
    const [agentsResult, promptsResult, costsResult, usageResult] = await Promise.all([
      getAiAgents(accessToken), getAiPrompts(accessToken), getAiCosts(accessToken), getAiUsage(accessToken),
    ]);
    setAgents(agentsResult.data || []);
    setPrompts(promptsResult.data || []);
    setCosts(costsResult.data || []);
    setUsage(usageResult.data || { total_usd: 0, llamadas: 0, detalle: [], desglose_mes: [] });
  }, [accessToken]);

  useEffect(() => {
    loadGovernance().catch((error) => alertError({ error, customMessage: "No se ha podido cargar la gobernanza de IA." }));
  }, [loadGovernance]);

  useEffect(() => {
    if (!selectedAgentId && agents.length) setSelectedAgentId(agents[0].id);
  }, [agents, selectedAgentId]);

  const selectedAgent = agents.find((agent) => agent.id === selectedAgentId);
  const isOfferImportAgent = selectedAgent?.codigo === "comercial-importacion-ofertas";

  useEffect(() => {
    if (!isOfferImportAgent) {
      setRuntimePrompt(null);
      return;
    }
    let cancelled = false;
    getAiRuntimePrompt(selectedAgentId, accessToken)
      .then((response) => { if (!cancelled) setRuntimePrompt(response.data); })
      .catch((error) => { if (!cancelled) alertError({ error, customMessage: "No se han podido cargar las instrucciones efectivas de importación." }); });
    return () => { cancelled = true; };
  }, [accessToken, isOfferImportAgent, selectedAgentId, prompts]);

  const selectedAgentPrompts = prompts.filter((prompt) => prompt.agente_id === selectedAgentId && prompt.estado !== "Archivado");
  const selectedPrompt = selectedAgentPrompts.find((prompt) => prompt.id === selectedPromptId)
    || (isOfferImportAgent && selectedAgentPrompts.find((prompt) => prompt.codigo === "comercial.importacion-ofertas.sistema" && prompt.estado === "Publicado"))
    || selectedAgentPrompts[0];
  const isUnusedImportDataPrompt = isOfferImportAgent && selectedPrompt?.codigo === "comercial.importacion-ofertas.datos";

  useEffect(() => {
    if (selectedPrompt) setPromptContent(selectedPrompt.contenido || "");
  }, [selectedPrompt?.id]);

  const savePromptVersion = async (publish) => {
    if (!selectedPrompt) return;
    setSavingPrompt(true);
    try {
      const draft = await createAiPromptDraft(selectedPrompt.id, { ...selectedPrompt, contenido: promptContent }, accessToken);
      if (publish) await publishAiPrompt(draft.data.id, accessToken);
      await loadGovernance();
      setSelectedPromptId(draft.data.id);
      alertSuccess({ customMessage: publish ? "Nueva versión publicada." : "Borrador de prompt creado." });
    } catch (error) {
      alertError({ error, customMessage: "No se ha podido guardar la versión del prompt." });
    } finally { setSavingPrompt(false); }
  };

  const setAgentActive = async (agent, activo) => {
    setSavingAgentId(agent.id);
    try {
      await updateAiAgentActive(agent.id, activo, accessToken);
      await loadGovernance();
      alertSuccess({ customMessage: `${agent.nombre} ${activo ? "activado" : "desactivado"}.` });
    } catch (error) {
      alertError({ error, customMessage: "No se ha podido actualizar el agente de campo." });
    } finally { setSavingAgentId(null); }
  };

  const updateCostField = (model, field, value) => {
    setCosts((current) => current.map((cost) => cost.modelo === model ? { ...cost, [field]: value } : cost));
  };

  const saveCost = async (cost) => {
    setSavingCost(cost.modelo);
    try {
      await updateAiCost(cost.modelo, {
        ...cost,
        entrada_usd_millon: cost.entrada_usd_millon === "" ? null : Number(cost.entrada_usd_millon),
        cache_entrada_usd_millon: cost.cache_entrada_usd_millon === "" ? null : Number(cost.cache_entrada_usd_millon),
        salida_usd_millon: cost.salida_usd_millon === "" ? null : Number(cost.salida_usd_millon),
        unidad_coste_usd: cost.unidad_coste_usd === "" ? null : Number(cost.unidad_coste_usd),
      }, accessToken);
      await loadGovernance();
      alertSuccess({ customMessage: `Tarifa de ${cost.modelo} guardada.` });
    } catch (error) {
      alertError({ error, customMessage: "No se ha podido guardar la tarifa." });
    } finally { setSavingCost(null); }
  };

  const validateConfig = ({ requireModel = true } = {}) => {
    if (!baseUrl?.trim()) {
      alertError({ error: "La URL es obligatoria." });
      return false;
    }

    if (!apiKey?.trim() && !apiKeyConfigured) {
      alertError({ error: "La API key es obligatoria." });
      return false;
    }

    if (requireModel && !defaultModel?.trim()) {
      alertError({ error: "El modelo es obligatorio." });
      return false;
    }

    return true;
  };

  const buildPayload = () => ({
    id: configId,
    base_url: baseUrl,
    api_key: apiKey,
    default_model: defaultModel,
    project_id: projectId,
  });

  const saveOpenAIConfig = async () => {
    if (!validateConfig()) {
      return;
    }

    setSavingConfig(true);

    try {
      const res = await updateOpenAIConfig(buildPayload(), accessToken);
      if (res.status === 204) {
        await loadOpenAIConfig();
        alertSuccess({
          customMessage: "Configuracion de OpenAI guardada con exito.",
        });
      }
    } catch (error) {
      alertError({
        error,
        customMessage: "No se ha podido guardar la configuracion de OpenAI.",
      });
    } finally {
      setSavingConfig(false);
    }
  };

  const saveAdminKey = async () => {
    if (!adminApiKey.trim() && !clearAdminApiKey) {
      alertError({ error: "Introduce una Admin API Key o marca su eliminación." });
      return;
    }
    setSavingAdminKey(true);
    try {
      await updateOpenAIAdminKey({ admin_api_key: adminApiKey, clear_admin_api_key: clearAdminApiKey }, accessToken);
      setAdminApiKey("");
      setClearAdminApiKey(false);
      await loadOpenAIConfig();
      alertSuccess({ customMessage: "Admin API Key actualizada." });
    } catch (error) {
      alertError({ error, customMessage: "No se ha podido guardar la Admin API Key." });
    } finally { setSavingAdminKey(false); }
  };

  const reconcileCosts = async () => {
    setReconciling(true);
    try {
      const result = await reconcileAiCurrentMonth(accessToken);
      setReconciliation(result.data);
      await loadGovernance();
    } catch (error) {
      alertError({ error, customMessage: "No se ha podido comprobar el coste oficial de OpenAI." });
    } finally { setReconciling(false); }
  };

  const handleGetModels = async () => {
    if (!validateConfig({ requireModel: false })) {
      return;
    }

    setLoadingModels(true);

    try {
      const res = await getOpenAIModels(buildPayload(), accessToken);
      if (res.status === 200) {
        setModels(res.data || []);
        setModelsDialogOpen(true);
      }
    } catch (error) {
      alertError({
        error,
        customMessage: "No se ha podido obtener la lista de modelos.",
      });
    } finally {
      setLoadingModels(false);
    }
  };

  const handleSelectModel = (modelId) => {
    setDefaultModel(modelId);
    setModelsDialogOpen(false);
    alertSuccess({
      customMessage: `Modelo seleccionado: ${modelId}`,
    });
  };

  const hasModels = useMemo(() => models.length > 0, [models]);

  if (isLoadingData) return <ContentSkeleton />;

  return (
    <>
      <Card>
        <CardHeader title="Configuración de IA" />
        <CardBody>
          <div className="d-flex border-bottom" style={{ gap: "1.5rem" }}>
            {[
              [0, "Conexión"],
              [1, "Agentes y prompts"],
              [2, "Costes"],
              [3, "Consumo"],
            ].map(([tab, label]) => (
              <button
                key={tab}
                type="button"
                className="border-0 bg-transparent font-weight-bolder pb-3"
                onClick={() => setActiveTab(tab)}
                style={{
                  color: activeTab === tab ? "#321ACB" : "#7E8299",
                  borderBottom: activeTab === tab ? "2px solid #321ACB" : "2px solid transparent",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </CardBody>
      </Card>
      {activeTab === 0 && (
      <Card>
        <CardHeader title="Configuracion de IA">
          <CardHeaderToolbar>
            <Button
              onClick={saveOpenAIConfig}
              variant="outlined"
              color="primary"
              disabled={savingConfig}
            >
              {savingConfig ? "Guardando..." : "Guardar configuracion"}
            </Button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <form autoComplete="off">
            <input
              type="text"
              name="fake_user"
              autoComplete="username"
              style={{ display: "none" }}
              tabIndex={-1}
            />
            <input
              type="password"
              name="fake_password"
              autoComplete="new-password"
              style={{ display: "none" }}
              tabIndex={-1}
            />

            <div className="row">
              <div className="col-md-12">
                <TextField
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  label="URL"
                  value={baseUrl}
                  onChange={(event) => setBaseUrl(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </div>
              <div className="col-md-12">
                <TextField
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  label="API Key"
                  type={showApiKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                  onInput={(event) => setApiKey(event.target.value)}
                  name="openai_api_key_manual"
                  autoComplete="off"
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    apiKeyConfigured
                      ? "API key cargada desde configuracion guardada."
                      : "Introduce la API key de OpenAI."
                  }
                  required
                  inputProps={{
                    autoComplete: "new-password",
                    "data-lpignore": "true",
                    "data-1p-ignore": "true",
                    "data-form-type": "other",
                    spellCheck: "false",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setShowApiKey((current) => !current)}
                        >
                          {showApiKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <div className="col-md-9">
                <TextField
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  label="Modelo"
                  value={defaultModel}
                  onChange={(event) => setDefaultModel(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Selecciona un modelo de la lista o introduce manualmente su identificador."
                  required
                />
              </div>
              <div
                className="col-md-3"
                style={{ display: "flex", alignItems: "center", marginTop: "8px" }}
              >
                <Button
                  onClick={handleGetModels}
                  variant="outlined"
                  color="primary"
                  disabled={loadingModels || savingConfig}
                  fullWidth
                >
                  {loadingModels ? "Obteniendo..." : "Obtener modelos"}
                </Button>
              </div>
              <div className="col-md-12">
                <TextField
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  label="ID del proyecto de OpenAI"
                  value={projectId}
                  onChange={(event) => setProjectId(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Necesario para comparar el gasto oficial del proyecto sin incluir el resto de la organización."
                />
              </div>
            </div>
          </form>
        </CardBody>
      </Card>
      )}

      {activeTab === 1 && <Card className="mt-5">
        <CardHeader title="Agentes y prompts operativos" />
        <CardBody>
          <div className="row">
            <div className="col-md-4">
              <div style={{ border: "1px solid #E4E6EF", borderRadius: 6 }}>
                <div style={{ padding: 16, fontWeight: 600, borderBottom: "1px solid #E4E6EF" }}>Agentes de administración</div>
                {agents.filter((agent) => agent.codigo === "comercial" || agent.codigo === "comercial-importacion-ofertas").map((agent) => <AgentRow key={agent.id} agent={agent} selected={selectedAgentId === agent.id} onSelect={() => { setSelectedAgentId(agent.id); setSelectedPromptId(null); }} />)}
                <div style={{ padding: "16px 16px 10px", fontWeight: 600, borderTop: "1px solid #E4E6EF", borderBottom: "1px solid #E4E6EF", background: "#FAFAFC" }}>Agentes de campo</div>
                {agents.filter((agent) => agent.codigo?.startsWith("campo-")).map((agent) => <AgentRow key={agent.id} agent={agent} selected={selectedAgentId === agent.id} onSelect={() => { setSelectedAgentId(agent.id); setSelectedPromptId(null); }} onActiveChange={setAgentActive} saving={savingAgentId === agent.id} />)}
              </div>
            </div>
            <div className="col-md-8">
              {selectedPrompt ? <>
                <div className="d-flex justify-content-between align-items-start mb-4"><div><h4 className="mb-1">{selectedPrompt.nombre}</h4><div className="text-muted">{selectedPrompt.codigo} · versión {selectedPrompt.version} · {selectedPrompt.estado}</div></div><div className="text-success">{agents.find((agent) => agent.id === selectedAgentId)?.nombre}</div></div>
                <div className="mb-3"><b>Otros prompts del agente</b><div className="mt-2">{selectedAgentPrompts.map((prompt) => <Button key={prompt.id} size="small" variant={selectedPrompt.id === prompt.id ? "contained" : "outlined"} color="primary" className="mr-2 mb-2" onClick={() => setSelectedPromptId(prompt.id)}>{prompt.nombre} v{prompt.version}</Button>)}</div></div>
                {isUnusedImportDataPrompt && <div className="alert alert-warning">Este prompt «datos» es histórico: el análisis actual construye el mensaje de usuario dinámicamente y no envía este texto. Su edición no cambiaría las importaciones.</div>}
                <TextField fullWidth multiline rows={22} variant="outlined" label="Instrucciones guardadas" value={promptContent} onChange={(event) => setPromptContent(event.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ className: "ai-agent-prompt", readOnly: isUnusedImportDataPrompt }} />
                <div className="text-muted mt-3">Variables: {selectedPrompt.variables_json || "[]"}. Guardar crea siempre una versión nueva, manteniendo intacta la publicada.</div>
                <div className="d-flex justify-content-end mt-4"><Button className="mr-2" variant="outlined" color="primary" disabled={savingPrompt || isUnusedImportDataPrompt} onClick={() => savePromptVersion(false)}>Crear borrador</Button><Button variant="contained" color="primary" disabled={savingPrompt || isUnusedImportDataPrompt} onClick={() => savePromptVersion(true)}>Publicar esta versión</Button></div>
                {isOfferImportAgent && <div className="mt-5">
                  <h5>Instrucciones efectivas del análisis de Excel</h5>
                  <div className="text-muted mb-3">{runtimePrompt?.version || "Cargando..."}. Incluye el prompt de sistema publicado y el contrato operativo fijo del código. Este es el texto que se envía al modelo; un borrador no interviene hasta publicarlo.</div>
                  <TextField fullWidth multiline rows={18} variant="outlined" label="Texto efectivo (solo lectura)" value={runtimePrompt?.instrucciones_efectivas || ""} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} inputProps={{ className: "ai-agent-prompt" }} />
                  <div className="text-muted mt-2">{runtimePrompt?.descripcion_entrada}</div>
                </div>}
              </> : <div className="text-muted">Selecciona un agente para consultar sus prompts.</div>}
            </div>
          </div>
        </CardBody>
      </Card>}

      {activeTab === 2 && <Card className="mt-5">
        <CardHeader title="Tarifas de IA">
          <CardHeaderToolbar><Button color="primary" variant="outlined" onClick={loadGovernance}>Actualizar</Button></CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <Table size="small">
            <TableHead><TableRow><TableCell>Modelo</TableCell><TableCell>Entrada / 1M</TableCell><TableCell>Caché / 1M</TableCell><TableCell>Salida / 1M</TableCell><TableCell>Audio ref.</TableCell><TableCell align="right">Acción</TableCell></TableRow></TableHead>
            <TableBody>{costs.map((cost) => <TableRow key={cost.modelo}>
              <TableCell><b>{cost.modelo}</b></TableCell>
              {["entrada_usd_millon", "cache_entrada_usd_millon", "salida_usd_millon", "unidad_coste_usd"].map((field) => <TableCell key={field}><TextField type="number" value={cost[field] ?? ""} onChange={(event) => updateCostField(cost.modelo, field, event.target.value)} inputProps={{ step: "0.000001", min: 0 }} /></TableCell>)}
              <TableCell align="right"><Button color="primary" variant="outlined" disabled={savingCost === cost.modelo} onClick={() => saveCost(cost)}>Guardar</Button></TableCell>
            </TableRow>)}</TableBody>
          </Table>
        </CardBody>
      </Card>}

      {activeTab === 3 && <Card className="mt-5">
        <CardHeader title="Consumo de IA">
          <CardHeaderToolbar><Button color="primary" variant="outlined" onClick={loadGovernance}>Actualizar</Button></CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <div className="row mb-5">
            <div className="col-md-3"><strong>Coste acumulado</strong><div style={{ fontSize: 24 }}>${Number(usage.total_usd || 0).toFixed(6)}</div></div>
            <div className="col-md-3"><strong>Llamadas registradas</strong><div style={{ fontSize: 24 }}>{usage.llamadas || 0}</div></div>
            <div className="col-md-6 text-muted">Cada llamada conserva agente, usuario, modalidad de entrada, contexto, tokens y la tarifa aplicada en ese momento. Imagen y PDF se facturan por los tokens reales del modelo; el audio puede usar además una tarifa por unidad.</div>
          </div>
          <div className="border rounded p-4 mb-5">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h5 className="mb-1">Comprobación con OpenAI</h5>
                <div className="text-muted">Compara el coste oficial acumulado del mes actual del proyecto configurado con la estimación registrada por Neo4.</div>
              </div>
              <Button color="primary" variant="outlined" disabled={reconciling || !adminApiKeyConfigured || !projectId} onClick={reconcileCosts}>
                {reconciling ? "Comprobando..." : "Comprobar con OpenAI"}
              </Button>
            </div>
            {!adminApiKeyConfigured && <div className="text-warning mb-3">Configura una Admin API Key para habilitar la comprobación oficial.</div>}
            {!projectId && <div className="text-warning mb-3">Guarda el ID del proyecto de OpenAI en la pestaña Conexión para poder filtrar el consumo oficial.</div>}
            <div className="row align-items-start">
              <div className="col-md-9">
                <TextField
                  fullWidth
                  margin="none"
                  variant="outlined"
                  label="Admin API Key para coste oficial"
                  type="password"
                  value={adminApiKey}
                  onChange={(event) => { setAdminApiKey(event.target.value); setClearAdminApiKey(false); }}
                  InputLabelProps={{ shrink: true }}
                  helperText={adminApiKeyConfigured ? "Clave de administración guardada. Déjala vacía para conservarla." : "La clave se cifra en el servidor y solo se usa para consultar el gasto agregado oficial."}
                  inputProps={{ autoComplete: "new-password", spellCheck: "false" }}
                />
              </div>
              <div className="col-md-3 d-flex align-items-center" style={{ minHeight: 56 }}>
                <Button color="primary" variant="outlined" fullWidth disabled={savingAdminKey || (!adminApiKey && !clearAdminApiKey)} onClick={saveAdminKey}>
                  {savingAdminKey ? "Guardando..." : "Guardar clave"}
                </Button>
              </div>
            </div>
            {adminApiKeyConfigured && <label className="d-flex align-items-center mt-3 mb-0">
              <Checkbox checked={clearAdminApiKey} onChange={(event) => setClearAdminApiKey(event.target.checked)} color="primary" />
              <span>Eliminar la Admin API Key guardada</span>
            </label>}
            {reconciliation && <div className="row mt-4">
              <div className="col-md-4"><strong>Estimado Neo4</strong><div>${Number(reconciliation.estimado_usd || 0).toFixed(6)}</div></div>
              <div className="col-md-4"><strong>Oficial OpenAI</strong><div>${Number(reconciliation.oficial_usd || 0).toFixed(6)}</div></div>
              <div className="col-md-4"><strong>Diferencia</strong><div>${Number(reconciliation.diferencia_usd || 0).toFixed(6)}</div></div>
            </div>}
          </div>
          <h5 className="mt-8">Desglose del mes en curso</h5>
          <div className="text-muted mb-3">Agrupado por operación, modelo y tipo de entrada para distinguir texto, imagen, PDF y futuras transcripciones.</div>
          <Table size="small">
            <TableHead><TableRow><TableCell>Operación</TableCell><TableCell>Modelo</TableCell><TableCell>Entrada</TableCell><TableCell align="right">Llamadas</TableCell><TableCell align="right">Entrada</TableCell><TableCell align="right">Caché</TableCell><TableCell align="right">Salida</TableCell><TableCell align="right">Coste</TableCell></TableRow></TableHead>
            <TableBody>{(usage.desglose_mes || []).map((item, index) => <TableRow key={`${item.operacion}-${item.modelo}-${item.modalidad}-${index}`}>
              <TableCell><b>{item.operacion}</b></TableCell><TableCell>{item.modelo}</TableCell><TableCell>{item.modalidad}</TableCell><TableCell align="right">{item.llamadas}</TableCell><TableCell align="right">{Number(item.entrada || 0).toLocaleString()}</TableCell><TableCell align="right">{Number(item.cache || 0).toLocaleString()}</TableCell><TableCell align="right">{Number(item.salida || 0).toLocaleString()}</TableCell><TableCell align="right">${Number(item.coste_usd || 0).toFixed(6)}</TableCell>
            </TableRow>)}{!(usage.desglose_mes || []).length && <TableRow><TableCell colSpan={8} className="text-muted">Aún no hay llamadas registradas este mes.</TableCell></TableRow>}</TableBody>
          </Table>
          <h5 className="mt-8">Últimas llamadas</h5>
          <Table size="small"><TableHead><TableRow><TableCell>Fecha</TableCell><TableCell>Agente</TableCell><TableCell>Usuario</TableCell><TableCell>Operación</TableCell><TableCell>Entrada</TableCell><TableCell>Contexto</TableCell><TableCell>Modelo</TableCell><TableCell>Tokens</TableCell><TableCell>Coste</TableCell></TableRow></TableHead>
            <TableBody>{(usage.detalle || []).map((call) => <TableRow key={call.id}><TableCell>{call.fecha ? new Date(call.fecha).toLocaleString() : "-"}</TableCell><TableCell>{call.agente}</TableCell><TableCell>{call.usuario}</TableCell><TableCell>{call.tipo_operacion || call.origen}</TableCell><TableCell>{call.modalidad_entrada || "Texto"}</TableCell><TableCell>{call.contexto || "-"}</TableCell><TableCell>{call.modelo}</TableCell><TableCell>{(call.tokens_entrada || 0) + (call.tokens_salida || 0)}</TableCell><TableCell>${Number(call.coste_usd || 0).toFixed(6)}</TableCell></TableRow>)}</TableBody>
          </Table>
        </CardBody>
      </Card>}

      <Dialog
        open={modelsDialogOpen}
        onClose={() => !loadingModels && setModelsDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Seleccionar modelo</DialogTitle>
        <DialogContent>
          {!hasModels ? (
            <div style={{ padding: "16px 0", color: "#7E8299" }}>
              No se han encontrado modelos disponibles.
            </div>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Modelo</TableCell>
                  <TableCell>Propietario</TableCell>
                  <TableCell align="right">Accion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {models.map((model) => (
                  <TableRow key={model.id} hover>
                    <TableCell>{model.id}</TableCell>
                    <TableCell>{model.owned_by || "-"}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleSelectModel(model.id)}
                      >
                        Seleccionar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModelsDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function AgentRow({ agent, selected, onSelect, onActiveChange, saving }) {
  return <div onClick={onSelect} style={{ cursor: "pointer", padding: 16, borderBottom: "1px solid #F1F1F5", background: selected ? "#F3F6F9" : "white", opacity: agent.activo ? 1 : 0.6 }}>
    <div className="d-flex align-items-start justify-content-between">
      <div><strong>{agent.nombre}</strong><div className="text-muted mt-1">{agent.descripcion}</div><div className={agent.activo ? "text-success mt-2" : "text-muted mt-2"}>{agent.activo ? "Activo" : "Desactivado"}</div></div>
      {onActiveChange && <Checkbox color="primary" checked={!!agent.activo} disabled={saving} onClick={(event) => event.stopPropagation()} onChange={(event) => onActiveChange(agent, event.target.checked)} inputProps={{ "aria-label": `${agent.activo ? "Desactivar" : "Activar"} ${agent.nombre}` }} />}
    </div>
  </div>;
}
